import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Staking = () => {
  const tiers = [
    {
      title: "Tier I – Entry",
      status: "Active",
      features: ["Easy access", "Lower APY", "For new holders"],
      color: "from-emerald-500/20 to-transparent",
      borderColor: "border-emerald-500/30 hover:border-emerald-500/60",
      iconColor: "text-emerald-400",
      glow: "group-hover:shadow-[0_0_40px_rgba(16,185,129,0.2)]"
    },
    {
      title: "Tier II – Ascension",
      status: "Active",
      features: ["365-day staking", "Higher rewards", "Long-term alignment"],
      color: "from-purple-500/20 to-transparent",
      borderColor: "border-purple-500/40 hover:border-purple-500/80",
      iconColor: "text-purple-400",
      popular: true,
      glow: "group-hover:shadow-[0_0_50px_rgba(168,85,247,0.3)]"
    },
    {
      title: "Tier III – GOD MODE",
      status: "Coming Soon",
      features: ["720-day lock", "Premium rewards", "Exclusive benefits"],
      color: "from-orange-500/10 to-transparent",
      borderColor: "border-orange-500/20",
      iconColor: "text-orange-400",
      glow: "group-hover:shadow-[0_0_30px_rgba(249,115,22,0.1)]"
    }
  ];

  return (
    <section className="py-32 px-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[500px] bg-purple-900/10 blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-bold mb-6 text-white"
          >
            Ascension <span className="text-gradient">Staking</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-400 max-w-2xl mx-auto font-light"
          >
            Lock your tokens on Blastpad. Elevate your status. Earn rewards.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -10 }}
              className={`relative rounded-[2rem] p-10 bg-[#0a0515] border ${tier.borderColor} backdrop-blur-xl group transition-all duration-500 ${tier.glow}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-b ${tier.color} rounded-[2rem] opacity-30 group-hover:opacity-100 transition-opacity duration-500`} />
              
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white px-6 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg">
                  Recommended
                </div>
              )}

              <div className="relative z-10">
                <div className="flex justify-between items-start mb-8">
                  <h3 className="text-2xl font-bold text-white">{tier.title}</h3>
                  {tier.status === 'Active' ? (
                    <SafeIcon name="CheckCircle" className="text-green-400 w-7 h-7 drop-shadow-[0_0_8px_rgba(74,222,128,0.5)]" />
                  ) : (
                    <span className="text-xs font-bold tracking-widest uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20 px-3 py-1.5 rounded-lg">Soon</span>
                  )}
                </div>

                <ul className="space-y-6 mb-10">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-4 text-slate-300 font-medium">
                      <div className={`p-1.5 rounded-md bg-[#ffffff0a] ${tier.iconColor}`}>
                        <SafeIcon name="Zap" className="w-4 h-4" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {tier.status === 'Active' ? (
                  <a 
                    href="https://blastpad.io/available-staking-pools" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full py-4 rounded-xl font-bold text-lg text-center bg-white text-[#030014] hover:scale-105 shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all duration-300"
                  >
                    Stake Now
                  </a>
                ) : (
                  <button className="w-full py-4 rounded-xl font-bold text-lg bg-white/5 text-slate-500 border border-white/5 cursor-not-allowed" disabled>
                    Locked
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-block glass-panel px-8 py-4 rounded-full neon-border">
            <p className="text-slate-300 font-medium text-lg">
              🔒 All rewards are distributed via <span className="text-cyan-400">Blastpad</span>. <span className="text-white font-bold">No inflation.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Staking;